import React, { useEffect, useState } from 'react';
import { Printer, Share2, Sparkles, Loader2, RefreshCw, Clock3, Info, Grid3X3 } from 'lucide-react';
import { formatCountdown, getNextNLCBDraw } from '../services/drawSchedule';
import { PredictionStrategy } from '../types';
import Tooltip from './Tooltip';

interface ActionButtonsProps {
  onGenerate: () => void;
  onPrint: () => void;
  onShare: () => void;
  onSync: () => void;
  isGenerating: boolean;
  isSyncing: boolean;
  hasPredictions: boolean;
  strategy: PredictionStrategy;
  onStrategyChange: (strategy: PredictionStrategy) => void;
  wheelMode: boolean;
  onWheelModeChange: (enabled: boolean) => void;
}

export const STRATEGY_HELP: Record<Exclude<PredictionStrategy, 'All Strategies'>, string> = {
  'Historical Hot Mix': 'Weights numbers that have appeared most often in the stored archive, then filters each line to the Lotto Plus 5/35 balance rules: sum 65–115, 2/3 or 3/2 high-low, and 2/3 or 3/2 odd-even.',
  'Overdue Balancer': 'Gives extra weight to numbers with the longest gaps since their last appearance while still considering overall frequency. Every line must pass the same sum, high-low, and odd-even balance filters.',
  'Range Balanced': 'Spreads selections across the 1–35 field rather than clustering in one area, then rejects lines outside the 65–115 sum window or the required 2/3–3/2 high-low and odd-even splits.',
  'Common Pair Mix': 'Starts from historically recurring neighboring/consecutive pairs, then builds a line with exactly one consecutive pair and no three-number run. The finished line must also pass the sum, high-low, and odd-even filters.',
  'Adaptive Ensemble': 'Scores thousands of balanced candidate lines using time-decayed frequency, overdue gaps, pair co-occurrence strength, and recent-number momentum, then returns the highest-scoring candidate. It is a historical heuristic, not a higher mathematical chance of winning.',
};

const strategies: PredictionStrategy[] = ['All Strategies', 'Historical Hot Mix', 'Overdue Balancer', 'Range Balanced', 'Common Pair Mix', 'Adaptive Ensemble'];

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onGenerate, onPrint, onShare, onSync, isGenerating, isSyncing, hasPredictions,
  strategy, onStrategyChange, wheelMode, onWheelModeChange,
}) => {
  const [, setTick] = useState(0);
  const nextDraw = getNextNLCBDraw();

  useEffect(() => {
    const timer = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const helpText = strategy === 'All Strategies'
    ? 'Generates one line from each of the five strategy models so you can compare their different selection logic.'
    : STRATEGY_HELP[strategy];

  return (
    <div className="fixed bottom-0 left-0 right-0 p-3 bg-slate-900/90 backdrop-blur-md border-t border-slate-800 z-50 no-print">
      <div className="max-w-7xl mx-auto flex flex-wrap justify-center items-center gap-3">
        <button
          onClick={onSync}
          disabled={isSyncing}
          className="flex items-center gap-2 px-4 py-3 rounded-full font-semibold bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700 transition disabled:opacity-60"
          title="Open the local browser importer and merge official NLCB results into the archive"
        >
          {isSyncing ? <Loader2 className="animate-spin" size={19} /> : <RefreshCw size={19} />}
          {isSyncing ? 'Syncing NLCB...' : 'Sync NLCB'}
        </button>

        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950/80 border border-slate-700">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Strategy</div>
            <select
              value={strategy}
              onChange={e => onStrategyChange(e.target.value as PredictionStrategy)}
              disabled={isGenerating || isSyncing}
              className="bg-transparent text-sm text-slate-200 font-semibold outline-none cursor-pointer"
              aria-label="Prediction strategy"
            >
              {strategies.map(item => <option key={item} value={item} className="bg-slate-900">{item}</option>)}
            </select>
          </div>
          <Tooltip text={helpText}>
            <span className="text-slate-500 hover:text-yellow-400 cursor-help"><Info size={16} /></span>
          </Tooltip>
        </div>

        <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-slate-300 cursor-pointer" title="Generate the 21 five-number combinations from a selected seven-number pool">
          <input
            type="checkbox"
            checked={wheelMode}
            onChange={e => onWheelModeChange(e.target.checked)}
            disabled={isGenerating || isSyncing}
            className="accent-yellow-500"
          />
          <Grid3X3 size={15} className="text-yellow-400" />
          7-number wheel (21)
        </label>

        <button
          onClick={onGenerate}
          disabled={isGenerating || isSyncing}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-slate-900 transition-all transform hover:scale-105 active:scale-95 shadow-lg ${isGenerating || isSyncing ? 'bg-slate-600 cursor-not-allowed' : 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 shadow-yellow-500/20'}`}
        >
          {isGenerating ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
          {isGenerating ? 'Analyzing...' : wheelMode ? 'Generate Wheel' : 'Generate Numbers'}
        </button>

        <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-slate-700 bg-slate-950/80 text-slate-300" title={`Next official Lotto Plus draw: ${nextDraw.label}`}>
          <Clock3 size={16} className="text-yellow-400" />
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Next Draw</div>
            <div className="font-mono text-xs font-bold text-yellow-300">{formatCountdown(nextDraw.at)}</div>
          </div>
        </div>

        {hasPredictions && <>
          <button onClick={onPrint} className="p-3 rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700 transition-colors" title="Print Predictions"><Printer size={20} /></button>
          <button onClick={onShare} className="p-3 rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700 transition-colors" title="Share"><Share2 size={20} /></button>
        </>}
      </div>
    </div>
  );
};

export default ActionButtons;
